import { useEffect, useMemo, useState } from 'react'
import { getAllPagesDataForMap } from '@widgets/Map/lib'

import { useExecuteCommandMutation } from '@redux/api'

import { getCommandRequestData } from '@helpers'
import { MapPointGroup, ObjectDataRecord } from '@types'

/**
 * Хук для загрузки данных точек из объектов
 * Загружает все страницы данных с автоматической пагинацией
 */
export const useLoadPointGroups = (pointGroups: MapPointGroup[]) => {
  const [executeCommand] = useExecuteCommandMutation()
  const [data, setData] = useState<Map<string, ObjectDataRecord[]>>(new Map())
  const [isLoading, setIsLoading] = useState(false)

  const groupsKey = useMemo(() => pointGroups.map(g => g.id).join(','), [pointGroups])

  useEffect(() => {
    if (pointGroups.length === 0) {
      setData(new Map())

      return
    }

    let mounted = true

    const loadData = async () => {
      setIsLoading(true)
      const newData = new Map<string, ObjectDataRecord[]>()

      for (const group of pointGroups) {
        if (!mounted) break

        try {
          const commandParams = {
            objectCode: group.objectCode,
            commandCode: group.getDataCommand.name,
            method: group.getDataCommand.httpMethod,
            transformResponseWithPagination: true,
            size: 1000,
            params: getCommandRequestData(group.getDataCommand.parameters ?? []),
            body: getCommandRequestData(group.getDataCommand.body ?? []),
          }

          const firstPageData = await executeCommand(commandParams).unwrap()

          if (!mounted) break

          const records = await getAllPagesDataForMap({
            firstPageData,
            executeCommand,
            commandParams,
          })

          if (!mounted) break

          newData.set(group.id, records)
          console.log(`Loaded ${records.length} points for "${group.title}"`)
        } catch (error) {
          console.error(`Error loading "${group.title}":`, error)
          newData.set(group.id, [])
        }
      }

      if (mounted) {
        setData(newData)
        setIsLoading(false)
      }
    }

    loadData()

    return () => {
      mounted = false
    }
  }, [groupsKey, executeCommand])

  return { data, isLoading, setData }
}
